import React, { useEffect, useState } from 'react';
import axios from 'axios';
import PublicToolsMenu from '../components/public/PublicToolsMenu';
import { setDeviceSeller, useTrackPage, trackSellerActivity } from '../utils/sellerTracking';
import PhysicalInventoryAdmin from './PhysicalInventoryAdmin';
import InventoryHistory from './InventoryHistory';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'seller_token';

const sax = axios.create({ baseURL: API });
sax.interceptors.request.use((cfg) => {
  const tok = localStorage.getItem(TOKEN_KEY);
  if (tok) cfg.headers.Authorization = `Bearer ${tok}`;
  return cfg;
});

function money(n) {
  return `$${Number(n || 0).toFixed(2)}`;
}

function SellerCatalogStats() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await sax.get('/public/seller/catalog-stats');
        if (!cancel) setData(data);
      } catch { /* ignore */ }
      finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, []);

  if (loading) return null;
  const totals = data?.totals || { visits: 0, wa_clicks: 0 };
  const daily = data?.daily || [];
  const maxV = Math.max(1, ...daily.map(d => Math.max(d.visits, d.wa_clicks)));
  const conversion = totals.visits > 0 ? Math.round((totals.wa_clicks / totals.visits) * 100) : 0;

  return (
    <section data-testid="seller-catalog-stats" className="mt-3">
      <div className="grid grid-cols-3 gap-2 mb-3">
        <div className="bg-white border-2 border-slate-200 rounded-xl p-3 text-center">
          <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Visitas</p>
          <p className="text-2xl font-black text-slate-900" data-testid="seller-stats-visits">{totals.visits}</p>
        </div>
        <div className="bg-white border-2 border-emerald-200 rounded-xl p-3 text-center">
          <p className="text-[10px] uppercase tracking-widest text-emerald-600 font-semibold">Clics WA</p>
          <p className="text-2xl font-black text-emerald-700" data-testid="seller-stats-wa">{totals.wa_clicks}</p>
        </div>
        <div className="bg-white border-2 border-slate-200 rounded-xl p-3 text-center">
          <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Conversión</p>
          <p className="text-2xl font-black text-slate-900">{conversion}%</p>
        </div>
      </div>
      {daily.length > 0 && totals.visits + totals.wa_clicks > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-3">
          <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Últimos 14 días</p>
          <div className="flex items-end justify-between gap-0.5 h-24">
            {daily.map(d => {
              const dLabel = d.date.slice(5); // MM-DD
              const hV = (d.visits / maxV) * 100;
              const hW = (d.wa_clicks / maxV) * 100;
              return (
                <div key={d.date} className="flex-1 flex flex-col items-center gap-0.5" title={`${dLabel} · ${d.visits} visitas · ${d.wa_clicks} clics WA`}>
                  <div className="w-full flex items-end justify-center gap-0.5 h-full">
                    <div className="w-1/2 bg-slate-300 rounded-t" style={{ height: `${hV}%` }} />
                    <div className="w-1/2 bg-emerald-500 rounded-t" style={{ height: `${hW}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex items-center justify-center gap-3 mt-2 text-[10px] text-slate-500">
            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-slate-300 rounded" />Visitas</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-emerald-500 rounded" />Clics WA</span>
          </div>
        </div>
      )}
    </section>
  );
}

function SellerCatalogLink({ seller }) {
  const [copied, setCopied] = useState(false);
  const slug = seller?.catalog_slug || seller?.id;
  if (!slug) return null;
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const link = `${origin}/catalogo?v=${slug}`;

  const copy = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
      } else {
        const ta = document.createElement('textarea');
        ta.value = link;
        ta.style.position = 'fixed'; ta.style.opacity = '0';
        document.body.appendChild(ta); ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch { /* ignore */ }
  };

  const shareOnWa = () => {
    const text = `¡Hola! Te comparto mi catálogo:\n${link}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank', 'noopener');
  };

  return (
    <section data-testid="seller-catalog-link">
      <h2 className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">🔗 Mi catálogo</h2>
      <div className="bg-white border-2 border-red-100 rounded-2xl p-4 shadow-sm">
        <p className="text-sm text-slate-700 mb-2">
          Comparte este enlace con tus clientes. Verán tu nombre arriba y un botón para escribirte directo por WhatsApp.
        </p>
        <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 mb-3">
          <span className="text-xs text-slate-700 break-all flex-1 font-mono" data-testid="seller-catalog-link-url">{link}</span>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={copy}
            className="py-2.5 rounded-xl font-semibold text-sm text-white bg-slate-800 hover:bg-slate-900 transition"
            data-testid="seller-catalog-link-copy"
          >{copied ? '✅ Copiado' : '🔗 Copiar link'}</button>
          <button
            onClick={shareOnWa}
            className="py-2.5 rounded-xl font-semibold text-sm text-white bg-emerald-500 hover:bg-emerald-600 transition"
            data-testid="seller-catalog-link-share-wa"
          >💬 Compartir por WhatsApp</button>
        </div>
        <SellerCatalogStats />
      </div>
    </section>
  );
}

function StatCard({ icon, label, sublabel, value, hint, testid, tone = 'default' }) {
  const toneCls = {
    default: 'bg-white border-slate-200',
    primary: 'bg-gradient-to-br from-red-600 to-red-700 text-white border-red-700 shadow-lg',
    success: 'bg-emerald-50 border-emerald-200',
    warning: 'bg-amber-50 border-amber-200',
  }[tone];
  const labelCls = tone === 'primary' ? 'text-white/80' : 'text-slate-500';
  const subCls = tone === 'primary' ? 'text-white/70' : 'text-slate-400';
  const valueCls = tone === 'primary' ? 'text-white' : 'text-slate-900';
  const hintCls = tone === 'primary' ? 'text-white/70' : 'text-slate-400';
  return (
    <div className={`border rounded-2xl p-4 ${toneCls}`} data-testid={testid}>
      <div className="flex items-center gap-2">
        <span className="text-xl">{icon}</span>
        <div className="flex-1 min-w-0">
          <p className={`text-[11px] uppercase tracking-wide font-semibold ${labelCls}`}>{label}</p>
          {sublabel && <p className={`text-[10px] ${subCls} mt-0.5`}>{sublabel}</p>}
        </div>
      </div>
      <p className={`text-2xl sm:text-3xl font-extrabold mt-1 ${valueCls}`}>{value}</p>
      {hint && <p className={`text-[11px] mt-1 ${hintCls}`}>{hint}</p>}
    </div>
  );
}

function LoginScreen({ onOk }) {
  const [nip, setNip] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!/^\d{4}$/.test(nip)) { setError('El NIP debe ser 4 dígitos'); return; }
    setLoading(true); setError('');
    try {
      const { data } = await sax.post('/public/seller/login', { nip });
      localStorage.setItem(TOKEN_KEY, data.token);
      // Reclamar dispositivo para este vendedor (usado por tracking de uso)
      if (data.seller?.id) setDeviceSeller(data.seller.id);
      onOk(data.seller);
    } catch (e2) {
      setError(e2?.response?.data?.detail || 'No se pudo iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center px-4">
      <PublicToolsMenu current="/vendedor" />
      <form onSubmit={submit} className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4" data-testid="seller-login-form">
        <div className="text-center">
          <div className="text-5xl mb-2">💼</div>
          <h1 className="text-xl font-bold text-slate-800">Portal del Vendedor</h1>
          <p className="text-sm text-slate-500 mt-1">Ingresa tu NIP para acceder</p>
        </div>
        <input
          type="password"
          inputMode="numeric"
          maxLength={4}
          placeholder="NIP (4 dígitos)"
          value={nip}
          onChange={(e) => setNip(e.target.value.replace(/\D/g, '').slice(0, 4))}
          className="w-full text-center text-2xl tracking-widest font-bold py-3 border-2 border-slate-200 focus:border-red-500 rounded-xl outline-none"
          data-testid="seller-nip-input"
          autoFocus
        />
        {error && <p className="text-sm text-rose-600 text-center" data-testid="seller-login-error">{error}</p>}
        <button
          type="submit"
          disabled={loading || nip.length !== 4}
          className="w-full bg-red-600 hover:bg-red-700 disabled:bg-slate-300 text-white font-semibold py-3 rounded-xl transition"
          data-testid="seller-login-btn"
        >
          {loading ? 'Entrando…' : 'Entrar'}
        </button>
        <p className="text-[11px] text-center text-slate-400">
          ¿No sabes tu NIP? Pídeselo al administrador.
        </p>
      </form>
    </div>
  );
}

export default function SellerPortal() {
  const [me, setMe] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showShipments, setShowShipments] = useState(false);
  const [shipments, setShipments] = useState(null);
  const [shipmentsLoading, setShipmentsLoading] = useState(false);
  const [shipFilter, setShipFilter] = useState('all'); // all|pending|paid

  // Trackear tiempo en el portal (solo se activa si el dispositivo está reclamado)
  useTrackPage('portal_view');

  // Si el usuario ya tenía sesión y recibimos `me`, aseguramos que el dispositivo
  // esté reclamado (por si borraron el localStorage pero mantienen sesión).
  useEffect(() => {
    if (me?.id) setDeviceSeller(me.id);
  }, [me?.id]);

  // Bootstrap: si hay token, obtener /me y luego dashboard
  useEffect(() => {
    const tok = localStorage.getItem(TOKEN_KEY);
    if (!tok) { setLoading(false); return; }
    (async () => {
      try {
        const meRes = await sax.get('/public/seller/me');
        setMe(meRes.data);
        await refreshDashboard();
      } catch {
        localStorage.removeItem(TOKEN_KEY);
        setMe(null);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [branchTab, setBranchTab] = useState(() => localStorage.getItem('seller_branch_tab') || 'morelos');
  useEffect(() => { localStorage.setItem('seller_branch_tab', branchTab); }, [branchTab]);

  const refreshDashboard = async () => {
    try {
      const { data } = await sax.get(`/public/seller/dashboard?branch=${branchTab}`);
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'No se pudo cargar el dashboard');
    }
  };

  // Refresh cuando cambia la sucursal seleccionada
  useEffect(() => {
    if (!me) return;
    refreshDashboard();
    // Re-fetch shipments e history si ya estaban cargados/visibles,
    // para que muestren datos de la sucursal recién seleccionada.
    if (shipments !== null || showShipments) {
      loadShipments(shipFilter, branchTab);
    }
    if (history !== null || showHistory) {
      loadHistory(branchTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchTab]);

  const loadHistory = async (targetBranch = branchTab) => {
    setHistoryLoading(true);
    try {
      const { data } = await sax.get(`/public/seller/history?limit=100&branch=${targetBranch}`);
      setHistory(data.entries || []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'No se pudo cargar el historial');
    } finally {
      setHistoryLoading(false);
    }
  };

  const loadShipments = async (filter = shipFilter, targetBranch = branchTab) => {
    setShipmentsLoading(true);
    try {
      const { data } = await sax.get(`/public/seller/shipments?status=${filter}&limit=100&branch=${targetBranch}`);
      setShipments(data.shipments || []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'No se pudieron cargar los envíos');
    } finally {
      setShipmentsLoading(false);
    }
  };

  const handleLogout = async () => {
    try { await sax.post('/public/seller/logout'); } catch { /* noop */ }
    localStorage.removeItem(TOKEN_KEY);
    setMe(null);
    setData(null);
  };

  const onLogin = async (seller) => {
    setMe(seller);
    setLoading(true);
    await refreshDashboard();
    setLoading(false);
  };

  if (!me) return <LoginScreen onOk={onLogin} />;

  // Modo Admin (PIN 0256): mostrar solo herramientas exclusivas del admin
  // dentro del portal público (Inventario físico). No exponemos comisiones,
  // historial, envíos, etc. porque no hay un vendedor asociado a esta sesión.
  if (me.is_admin_pin) {
    return <AdminPinPortal me={me} onLogout={handleLogout} />;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <PublicToolsMenu current="/vendedor" />

      <header
        className="bg-gradient-to-br from-red-600 to-red-700 text-white shadow"
        style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 4.5rem)' }}
      >
        <div className="max-w-4xl mx-auto px-4 pb-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[11px] uppercase tracking-widest opacity-80">Portal Vendedor</p>
              <h1 className="text-2xl sm:text-3xl font-bold" data-testid="seller-name">💼 Hola, {me.name}</h1>
              <p className="text-xs opacity-80 mt-1">Aquí va tu progreso — enfocado en lo que ganas, no en lo que vendes.</p>
            </div>
            <button
              onClick={handleLogout}
              className="text-xs bg-red-800/50 hover:bg-red-800 px-3 py-1.5 rounded-full flex-shrink-0"
              data-testid="seller-logout-btn"
            >Salir</button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-5 space-y-5">
        {/* Tabs de sucursal (multi-sucursal) */}
        <div className="flex items-center gap-1 rounded-lg bg-slate-100 p-1 text-xs w-fit" data-testid="seller-branch-tabs">
          <span className="text-[10px] uppercase text-slate-500 px-1.5">Sucursal:</span>
          {[
            { id: 'morelos', label: 'Morelos' },
            { id: 'america', label: 'América' },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setBranchTab(t.id)}
              className={`px-3 py-1 rounded-md font-medium ${branchTab === t.id ? 'bg-white shadow text-slate-800' : 'text-slate-500'}`}
              data-testid={`seller-branch-tab-${t.id}`}
            >{t.label}</button>
          ))}
        </div>

        {error && (
          <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-lg p-3 text-sm">{error}</div>
        )}

        {loading && !data && <p className="text-center text-slate-500 py-8">Cargando…</p>}

        {data && (
          <>
            {/* Mi enlace de catálogo (para compartir con clientes) */}
            <SellerCatalogLink seller={me} />

            {/* Comisiones */}
            <section>
              <h2 className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">💵 Tus comisiones</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <StatCard
                  icon="🌞" label="Hoy"
                  sublabel={data.date_labels?.today}
                  value={money(data.commissions.today.total)}
                  hint={`${data.commissions.today.count} comisión(es)`}
                  testid="comm-today" tone="primary"
                />
                <StatCard
                  icon="📅" label="Esta semana"
                  sublabel={data.date_labels?.week}
                  value={money(data.commissions.week.total)}
                  hint={`${data.commissions.week.count} comisión(es)`}
                  testid="comm-week"
                />
                <StatCard
                  icon="🗓️" label="Este mes"
                  sublabel={data.date_labels?.month}
                  value={money(data.commissions.month.total)}
                  hint={`${data.commissions.month.count} comisión(es)`}
                  testid="comm-month"
                />
              </div>
              <p className="text-[11px] text-slate-400 mt-2 italic text-center" data-testid="comm-disclaimer">
                Las comisiones pueden variar por fechas de corte en cada mes.
              </p>
            </section>

            {/* Podio */}
            {data.podium && data.podium.total_sellers > 1 && (
              <section>
                <h2 className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">🏆 Podio del mes</h2>
                <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid="podium">
                  {/* Podio top 3 estilo bloque */}
                  <div className="flex items-end justify-center gap-3 mb-4">
                    {/* 2do */}
                    {data.podium.top3[1] ? (
                      <div className="flex-1 text-center" data-testid="podium-2">
                        <p className="text-2xl">🥈</p>
                        <p className={`text-xs font-semibold truncate ${data.podium.top3[1].is_me ? 'text-red-600' : 'text-slate-700'}`}>{data.podium.top3[1].name}</p>
                        <p className="text-[11px] text-slate-500">{money(data.podium.top3[1].total)}</p>
                        <div className="bg-slate-300 rounded-t-lg mt-1 h-16" />
                      </div>
                    ) : <div className="flex-1"/>}
                    {/* 1ro */}
                    {data.podium.top3[0] ? (
                      <div className="flex-1 text-center" data-testid="podium-1">
                        <p className="text-3xl">🥇</p>
                        <p className={`text-sm font-bold truncate ${data.podium.top3[0].is_me ? 'text-red-600' : 'text-slate-800'}`}>{data.podium.top3[0].name}</p>
                        <p className="text-xs text-slate-600 font-semibold">{money(data.podium.top3[0].total)}</p>
                        <div className="bg-gradient-to-t from-yellow-400 to-yellow-300 rounded-t-lg mt-1 h-24" />
                      </div>
                    ) : <div className="flex-1"/>}
                    {/* 3ro */}
                    {data.podium.top3[2] ? (
                      <div className="flex-1 text-center" data-testid="podium-3">
                        <p className="text-2xl">🥉</p>
                        <p className={`text-xs font-semibold truncate ${data.podium.top3[2].is_me ? 'text-red-600' : 'text-slate-700'}`}>{data.podium.top3[2].name}</p>
                        <p className="text-[11px] text-slate-500">{money(data.podium.top3[2].total)}</p>
                        <div className="bg-amber-600 rounded-t-lg mt-1 h-12" />
                      </div>
                    ) : <div className="flex-1"/>}
                  </div>
                  {/* Mi posición */}
                  <div className="border-t border-slate-100 pt-3 text-center" data-testid="podium-me">
                    <p className="text-xs text-slate-500">Tu posición del mes</p>
                    <p className="text-2xl font-bold text-slate-800 mt-0.5">
                      #{data.podium.my_position || '—'} <span className="text-sm text-slate-400 font-medium">de {data.podium.total_sellers}</span>
                    </p>
                    <p className="text-sm text-emerald-700 font-semibold mt-0.5">{money(data.podium.my_total)}</p>
                    <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                      {data.podium.ahead ? (
                        <div className="bg-blue-50 border border-blue-100 rounded-lg p-2">
                          <p className="text-blue-600 font-semibold text-[10px] uppercase">Para rebasar a</p>
                          <p className="font-bold text-slate-800 truncate">{data.podium.ahead.name}</p>
                          <p className="text-blue-700">Te faltan <b>{money(data.podium.ahead.gap)}</b></p>
                        </div>
                      ) : <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2 text-center col-span-1"><p className="font-semibold text-yellow-700">👑 Vas 1er lugar</p></div>}
                      {data.podium.behind && (
                        <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-2">
                          <p className="text-emerald-600 font-semibold text-[10px] uppercase">Adelantado a</p>
                          <p className="font-bold text-slate-800 truncate">{data.podium.behind.name}</p>
                          <p className="text-emerald-700">Por <b>{money(data.podium.behind.gap)}</b></p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </section>
            )}

            {/* Envíos */}
            <section>
              <h2 className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">🚚 Envíos que has cerrado</h2>
              <div className="grid grid-cols-3 gap-3">
                <StatCard icon="🌞" label="Hoy" sublabel={data.date_labels?.today} value={data.shipments.today} testid="ship-today" />
                <StatCard icon="📅" label="Semana" sublabel={data.date_labels?.week} value={data.shipments.week} testid="ship-week" />
                <StatCard icon="🗓️" label="Mes" sublabel={data.date_labels?.month} value={data.shipments.month} testid="ship-month" />
              </div>
              {data.shipments.pending_count > 0 && (
                <div className="mt-3 bg-white border-2 border-amber-300 rounded-2xl p-4 flex items-center gap-4" data-testid="ship-pending">
                  <div className="text-3xl">⏳</div>
                  <div className="flex-1">
                    <p className="text-[11px] uppercase tracking-wide text-amber-700 font-semibold">Envíos pendientes de pago</p>
                    <p className="text-2xl font-bold text-amber-800">{data.shipments.pending_count}</p>
                    <p className="text-xs text-slate-600 mt-0.5">
                      Comisión proyectada al cobrar: <b className="text-emerald-700">{money(data.shipments.pending_commission)}</b>
                    </p>
                  </div>
                </div>
              )}
            </section>

            {/* Top productos */}
            {data.top_products.length > 0 && (
              <section>
                <h2 className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">⭐ Tus productos top del mes</h2>
                <ul className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100" data-testid="top-products">
                  {data.top_products.map((p, idx) => (
                    <li key={idx} className="p-3 flex items-center gap-3">
                      <div className={`w-8 h-8 flex-shrink-0 rounded-full flex items-center justify-center font-bold text-white ${idx === 0 ? 'bg-yellow-500' : idx === 1 ? 'bg-slate-400' : idx === 2 ? 'bg-amber-600' : 'bg-slate-300'}`}>
                        {idx + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-800 break-words">{p.name}</p>
                        <p className="text-[11px] text-slate-500">{p.quantity} pza(s) vendidas</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="font-bold text-emerald-700">{money(p.commission)}</p>
                        <p className="text-[10px] text-slate-400">comisión</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Coach: consejos y motivación */}
            {data.tips && data.tips.length > 0 && (
              <section>
                <h2 className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">🎯 Zona del coach</h2>
                <ul className="space-y-2" data-testid="coach-tips">
                  {data.tips.map((t, i) => (
                    <li key={i} className="bg-white border border-slate-200 rounded-xl p-3 flex items-start gap-3" data-testid={`coach-tip-${i}`}>
                      <span className="text-2xl flex-shrink-0">{t.emoji}</span>
                      <p className="text-sm text-slate-700 leading-snug">{t.text}</p>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Envíos creados a mi nombre */}
            <section>
              <button
                onClick={() => {
                  const willOpen = !showShipments;
                  setShowShipments(willOpen);
                  if (willOpen) {
                    if (!shipments) loadShipments();
                    trackSellerActivity('shipments_toggle');
                  }
                }}
                className="w-full bg-white border border-slate-200 rounded-2xl p-4 flex items-center justify-between hover:bg-slate-50 transition"
                data-testid="ship-list-toggle"
              >
                <div className="text-left">
                  <p className="font-semibold text-slate-800">🚚 Mis envíos</p>
                  <p className="text-xs text-slate-500">Todos los envíos creados a tu nombre</p>
                </div>
                <span className="text-slate-400 text-xl">{showShipments ? '▲' : '▼'}</span>
              </button>
              {showShipments && (
                <div className="mt-2 space-y-2" data-testid="ship-list">
                  {/* Filtros */}
                  <div className="flex items-center gap-1 rounded-lg bg-slate-100 p-1 text-xs">
                    {[['all','Todos'],['pending','Pendientes'],['paid','Cobrados']].map(([val, lbl]) => (
                      <button
                        key={val}
                        onClick={() => { setShipFilter(val); loadShipments(val); }}
                        className={`flex-1 px-3 py-1.5 rounded-md font-medium ${shipFilter === val ? 'bg-white shadow text-slate-800' : 'text-slate-500'}`}
                        data-testid={`ship-filter-${val}`}
                      >{lbl}</button>
                    ))}
                  </div>
                  {shipmentsLoading && <p className="text-sm text-slate-500 text-center py-4">Cargando…</p>}
                  {shipments && shipments.length === 0 && <p className="text-sm text-slate-400 text-center py-4">Sin envíos en este filtro.</p>}
                  {shipments && shipments.map((s) => {
                    const statusInfo = {
                      por_asignar: { cls: 'bg-amber-100 text-amber-700', label: '⏳ Sin asignar' },
                      assigned:    { cls: 'bg-blue-100 text-blue-700',    label: '🚚 Asignado' },
                      paid:        { cls: 'bg-emerald-100 text-emerald-700', label: '✅ Cobrado' },
                    }[s.status] || { cls: 'bg-slate-100 text-slate-700', label: s.status };
                    return (
                      <div key={s.id} className="bg-white border border-slate-200 rounded-xl p-3" data-testid={`ship-entry-${s.id}`}>
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${statusInfo.cls}`}>{statusInfo.label}</span>
                          {s.folio && <span className="text-[11px] text-slate-500 font-mono">#{s.folio}</span>}
                          <span className="text-[11px] text-slate-500">
                            {s.created_at ? new Date(s.created_at).toLocaleString('es-MX', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}
                          </span>
                          <div className="flex-1" />
                          <span className={`text-sm font-bold ${s.status === 'paid' ? 'text-emerald-700' : 'text-slate-500'}`}>{money(s.total_commission)}</span>
                        </div>
                        {s.client_name && <p className="text-xs text-slate-700"><b>👤</b> {s.client_name}{s.client_phone ? ` · ${s.client_phone}` : ''}</p>}
                        {s.shipping_address && <p className="text-xs text-slate-500 mt-0.5">📍 {s.shipping_address}</p>}
                        {s.deliverer_name && <p className="text-xs text-slate-500">🚚 Entregador: {s.deliverer_name}</p>}
                        <ul className="mt-2 space-y-0.5 text-xs text-slate-700">
                          {s.items.map((it, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <span className="text-slate-400 flex-shrink-0">•</span>
                              <span className="flex-1 break-words">
                                <b>{it.quantity}×</b> {it.name}{it.variant_name ? ` — ${it.variant_name}` : ''}
                              </span>
                              <span className="text-emerald-700 font-medium flex-shrink-0">{money(it.commission_total)}</span>
                            </li>
                          ))}
                        </ul>
                        {s.status !== 'paid' && (
                          <p className="text-[10px] text-amber-700 mt-2 italic">La comisión se paga cuando el envío se cobre.</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            {/* Historial de ventas */}
            <section>
              <button
                onClick={() => {
                  const willOpen = !showHistory;
                  setShowHistory(willOpen);
                  if (willOpen) {
                    if (!history) loadHistory();
                    trackSellerActivity('history_toggle');
                  }
                }}
                className="w-full bg-white border border-slate-200 rounded-2xl p-4 flex items-center justify-between hover:bg-slate-50 transition"
                data-testid="history-toggle"
              >
                <div className="text-left">
                  <p className="font-semibold text-slate-800">📜 Historial de ventas</p>
                  <p className="text-xs text-slate-500">Cada venta con productos y comisión (sin monto de venta)</p>
                </div>
                <span className="text-slate-400 text-xl">{showHistory ? '▲' : '▼'}</span>
              </button>
              {showHistory && (
                <div className="mt-2 space-y-2" data-testid="history-list">
                  {historyLoading && <p className="text-sm text-slate-500 text-center py-4">Cargando historial…</p>}
                  {history && history.length === 0 && <p className="text-sm text-slate-400 text-center py-4">Aún no tienes comisiones registradas.</p>}
                  {history && history.map((e) => (
                    <div key={e.sale_id} className="bg-white border border-slate-200 rounded-xl p-3" data-testid={`history-entry-${e.sale_id}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${e.source === 'shipment' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                          {e.source === 'shipment' ? '🚚 Envío' : '🛒 Mostrador'}
                        </span>
                        <span className="text-[11px] text-slate-500">
                          {e.created_at ? new Date(e.created_at).toLocaleString('es-MX', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}
                        </span>
                        {e.folio && <span className="text-[11px] text-slate-400">#{e.folio}</span>}
                        <div className="flex-1" />
                        <span className="text-sm font-bold text-emerald-700">{money(e.commission_total)}</span>
                      </div>
                      {e.client_name && <p className="text-xs text-slate-600 mb-1">👤 {e.client_name}</p>}
                      <ul className="space-y-0.5 text-xs text-slate-700">
                        {e.items.map((it, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <span className="text-slate-400 flex-shrink-0">•</span>
                            <span className="flex-1 break-words">
                              <b>{it.quantity}×</b> {it.name}
                            </span>
                            <span className="text-emerald-700 font-medium flex-shrink-0">{money(it.commission)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <p className="text-center text-[10px] text-slate-400 pt-4">
              Actualizado {new Date(data.computed_at).toLocaleString('es-MX')}
            </p>
          </>
        )}
      </main>
    </div>
  );
}


function AdminPinPortal({ me, onLogout }) {
  const [screen, setScreen] = useState('menu'); // menu | inventory
  return (
    <div className="min-h-screen bg-slate-50">
      <PublicToolsMenu current="/vendedor" />
      <header
        className="bg-gradient-to-br from-slate-900 to-slate-800 text-white shadow"
        style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 4.5rem)' }}
      >
        <div className="max-w-4xl mx-auto px-4 pb-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[11px] uppercase tracking-widest opacity-70">Modo administrador</p>
              <h1 className="text-2xl sm:text-3xl font-bold" data-testid="admin-pin-header">🔐 Herramientas del admin</h1>
              <p className="text-xs opacity-80 mt-1">Funciones exclusivas — sesión activa con PIN 0256.</p>
            </div>
            <button
              onClick={onLogout}
              className="text-xs bg-slate-700 hover:bg-slate-600 px-3 py-1.5 rounded-full flex-shrink-0"
              data-testid="admin-pin-logout"
            >Salir</button>
          </div>
        </div>
      </header>

      <main className="py-5">
        {screen === 'menu' && (
          <div className="max-w-4xl mx-auto px-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                onClick={() => setScreen('inventory')}
                className="text-left bg-white border-2 border-slate-200 hover:border-emerald-400 rounded-2xl p-5 transition"
                data-testid="admin-menu-inventory"
              >
                <div className="text-3xl mb-2">📋</div>
                <p className="font-bold text-slate-900">Inventario</p>
                <p className="text-xs text-slate-500 mt-1">
                  Contar físicamente Morelos y América, comparar con el sistema y generar PDF.
                </p>
              </button>
              <button
                onClick={() => setScreen('history')}
                className="text-left bg-white border-2 border-slate-200 hover:border-sky-400 rounded-2xl p-5 transition"
                data-testid="admin-menu-history"
              >
                <div className="text-3xl mb-2">📚</div>
                <p className="font-bold text-slate-900">Historial de inventarios</p>
                <p className="text-xs text-slate-500 mt-1">
                  Revisa los inventarios ya firmados, con o sin ajustes, y descarga sus PDF.
                </p>
              </button>
            </div>
          </div>
        )}
        {screen === 'inventory' && (
          <PhysicalInventoryAdmin onExit={() => setScreen('menu')} />
        )}
        {screen === 'history' && (
          <InventoryHistory onExit={() => setScreen('menu')} />
        )}
      </main>
    </div>
  );
}
